'use server'

import { apiRequest, ApiResponse } from '../request'

export interface StockOverview {
    totalMedications: number
    controlledMedications: number
    activeBatches: number
    lowStockCount: number
    expiringCount: number
    pendingDispensations: number
    temperatureAlerts: number
    pendingSngpc: number
}

export interface StockValue {
    totalValue: number
    totalItems: number
    batchCount: number
}

export interface LowStockItem {
    id: number
    name: string
    genericName?: string
    currentStock: number
    minimumStock: number
    deficit: number
}

export interface ExpiringBatch {
    id: number
    batchNumber: string
    expirationDate: string
    currentQuantity: number
    medication: {
        id: number
        name: string
    }
}

export interface MovementSummary {
    type: string
    count: number
    totalQuantity: number
}

export interface DispensationStats {
    total: number
    completed: number
    cancelled: number
    pending: number
    completionRate: number
    byStatus: {
        status: string
        _count: number
    }[]
}

export interface TopDispensedMedication {
    medication: {
        id: number
        name: string
        genericName?: string
        unit: string
    }
    totalDispensed: number
    dispensationCount: number
}

export interface TemperatureStatus {
    id: number
    name: string
    type: string
    currentTemperature?: number
    currentHumidity?: number
    lastReading?: string
    minTemp: number
    maxTemp: number
    isInRange: boolean | null
    activeAlerts: number
}

export interface SngpcSummary {
    pendingMovements: number
    lastTransmission?: {
        id: number
        status: string
        totalMovements: number
        createdAt: string
        transmittedAt?: string
    }
    controlledMedicationsCount: number
}

export interface ABCAnalysisItem {
    medication?: {
        id: number
        name: string
    }
    quantity: number
    cost: number
    percentage: number
    classification: 'A' | 'B' | 'C'
    cumulativePercentage: number
}

export interface ABCAnalysis {
    items: ABCAnalysisItem[]
    summary: {
        classA: number
        classB: number
        classC: number
        totalCost: number
    }
}

export interface InventoryTurnover {
    periodDays: number
    totalConsumption: number
    averageInventory: number
    turnoverRate: number
    daysCoverage: number
}

// Dashboard endpoints
export async function getStockOverview(): Promise<ApiResponse<StockOverview>> {
    return apiRequest<StockOverview>('stock/dashboard/overview', {})
}

export async function getStockValue(): Promise<ApiResponse<StockValue>> {
    return apiRequest<StockValue>('stock/dashboard/stock-value', {})
}

export async function getLowStockItems(
    limit?: number
): Promise<ApiResponse<LowStockItem[]>> {
    return apiRequest<LowStockItem[]>('stock/dashboard/low-stock', {
        params: { limit },
    })
}

export async function getExpiringBatchesForDashboard(
    days?: number,
    limit?: number
): Promise<ApiResponse<ExpiringBatch[]>> {
    return apiRequest<ExpiringBatch[]>('stock/dashboard/expiring', {
        params: { days, limit },
    })
}

export async function getMovementSummary(
    days?: number
): Promise<ApiResponse<MovementSummary[]>> {
    return apiRequest<MovementSummary[]>('stock/dashboard/movement-summary', {
        params: { days },
    })
}

export async function getDispensationStats(
    days?: number
): Promise<ApiResponse<DispensationStats>> {
    return apiRequest<DispensationStats>('stock/dashboard/dispensation-stats', {
        params: { days },
    })
}

export async function getTopDispensedMedications(
    days?: number,
    limit?: number
): Promise<ApiResponse<TopDispensedMedication[]>> {
    return apiRequest<TopDispensedMedication[]>(
        'stock/dashboard/top-dispensed',
        { params: { days, limit } }
    )
}

export async function getTemperatureStatus(): Promise<
    ApiResponse<TemperatureStatus[]>
> {
    return apiRequest<TemperatureStatus[]>(
        'stock/dashboard/temperature-status',
        {}
    )
}

export async function getSngpcSummary(): Promise<ApiResponse<SngpcSummary>> {
    return apiRequest<SngpcSummary>('stock/dashboard/sngpc-summary', {})
}

export async function getABCAnalysis(
    days?: number
): Promise<ApiResponse<ABCAnalysis>> {
    return apiRequest<ABCAnalysis>('stock/dashboard/abc-analysis', {
        params: { days },
    })
}

export async function getInventoryTurnover(
    days?: number
): Promise<ApiResponse<InventoryTurnover>> {
    return apiRequest<InventoryTurnover>('stock/dashboard/inventory-turnover', {
        params: { days },
    })
}
